import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    Link,
    Grid,
    Chip
} from '@mui/material';
import {
    Code,
    Language,
    GitHub,
    Email,
    Web
} from '@mui/icons-material';
import { useAppSelector } from 'src/hooks/redux';

interface AboutDialogProps {
    open: boolean;
    onClose: () => void;
}

const AboutDialog: React.FC<AboutDialogProps> = ({ open, onClose }) => {
    const { theme } = useAppSelector(state => state.ui);
    const isDark = theme === 'dark';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box
                        component="img"
                        src="/telixia-logo-color-736px.png"
                        alt="Telix Lab Logo"
                        sx={{
                            height: 120,
                            width: 'auto',
                            maxWidth: '80%',
                            objectFit: 'contain',
                        }}
                    />
                    <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                        Telix Lab
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Virtual Lab Platform for Robotics & AI
                    </Typography>
                    <Chip
                        label="Version 0.1.0"
                        color="primary"
                        size="small"
                        sx={{ mt: 1 }}
                    />
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" paragraph>
                    A browser-based virtual lab platform for robotics and AI software development.
                    Supports ROS2, Gazebo, Isaac Lab, and TensorFlow integration.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    Built with:
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    {['React', 'TypeScript', 'Material-UI', 'Three.js', 'React Flow', 'Redux Toolkit'].map((tech) => (
                        <Grid key={tech}>
                            <Chip
                                label={tech}
                                size="small"
                                variant="outlined"
                            />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    Features:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <Typography component="li" variant="body2" color="text.secondary" paragraph>
                        Visual Flow Editor for ROS2 nodes
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary" paragraph>
                        3D Simulation Environment
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary" paragraph>
                        Robot Control & Teleoperation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary" paragraph>
                        Module Marketplace & Extensibility
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                        Real-time Monitoring & Debugging
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Web fontSize="small" color="action" />
                        <Link
                            href="https://telixia.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                        >
                            https://telixia.com
                        </Link>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GitHub fontSize="small" color="action" />
                        <Link
                            href="https://github.com/telixia"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                        >
                            GitHub Repository
                        </Link>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" color="action" />
                        <Link
                            href="mailto:support@telixia.com"
                            underline="hover"
                        >
                            support@telixia.com
                        </Link>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="contained" fullWidth>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AboutDialog;

